// Refactored by j-funk into functional component 2023/09
/* eslint-disable react/prop-types */
import React, { useCallback, useState } from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';

import { ProgressBar, Alert, Col } from 'react-bootstrap';
import { connect } from 'react-redux';
import { useDeepCompareEffect } from 'react-use';
import s from './MultiNetwork.css';

import {
  selectIntegration,
  selectDatatypes,
  addDatatype,
  removeDatatype,
  addNetwork,
  fetchNetworkWithTerms,
} from '../../actions/NetworkActions';
import { setGenes } from '../../actions/actions';

import NetworkApp from '../NetworkApp';
import NetworkSelectionToolbar from '../NetworkSelectionToolbar';
import ModalDialog from '../ModalDialog';

/*
 * The container component of the network selection toolbar.
 * Injected with current state.
 */

const NetworkSelection = connect(
  (state, props) => {
    const { group } = props;
    const selection = state.networkSelection[group];
    return {
      // Currently selected integration
      selectedIntegration: selection && selection.selectedIntegration,

      // Currently selected array of data types
      selectedDatatypes: selection && selection.selectedDatatypes,

      // Currently selected genes to query
      genes: (selection && selection.selectedGenes) || [],
    };
  },
  (dispatch, props) => {
    const { group } = props;
    const id = { group };
    return {
      onIntegrationChange: integration => {
        dispatch(selectIntegration(integration, id));
      },
      onGenesChange: genes => {
        dispatch(setGenes(genes, id));
      },
      onDatatypeChange: (datatype, idx, added, integration, genes, types) => {
        if (added) {
          dispatch(addDatatype(datatype, id));
        } else {
          types.forEach((type, idx) => {
            if (type === datatype) dispatch(removeDatatype(id, idx));
          });
        }
      },
    };
  },
)(NetworkSelectionToolbar);

/*
 * Presentational component of the multi network UI
 */
function MultiNetworkView(props) {
  const [integrationScores, setIntegrationScores] = useState({});
  const {
    datatypes,
    group,
    groupTitle,
    integrations,
    isLoading,
    networks,
    onNetworkAdd,
    relevantNetworks,
    selectedIntegration,
  } = props;

  useDeepCompareEffect(() => {
    const scores = relevantNetworks.reduce((accumulator, currentNetwork) => {
      const updatedAccumulator = { ...accumulator };
      if (currentNetwork.context.term) {
        updatedAccumulator[currentNetwork.context.term.identifier] =
          currentNetwork.score;
      }
      return updatedAccumulator;
    }, {});
    setIntegrationScores(scores);
  }, [relevantNetworks]);

  const newOnNetworkAdd = useCallback(
    (selectedIntegration, datatypes, genes) => {
      if (onNetworkAdd)
        return onNetworkAdd(selectedIntegration, datatypes, genes, networks);
      return onNetworkAdd;
    },
    [onNetworkAdd, networks],
  );

  const onNetworkFilter = (net, genesrm, genesadd) => {
    // Redraw all other networks -- other networks' UI affected by
    // this change
    if (net.d3 && (genesrm[0].length || genesadd[0].length)) {
      net.d3.bindNetworks().forEach(bindNet => {
        if (bindNet !== net.d3) bindNet.draw();
      });
    }
  };

  const multiNet = networks.length > 1;
  const vertical = multiNet;

  // Don't allow adding additional networks if
  // 1) There are already 2 networks or
  // 2) There are no integrations to select from or
  // 3) There is no selected integration to spawn a network or
  // 4) The network is currently loading
  const disabled =
    networks.length > 1 ||
    !integrations.length ||
    !selectedIntegration ||
    isLoading;

  const networkapps = (net, idx) => (
    <Col md={multiNet ? 6 : 12} key={net.id}>
      <NetworkApp
        key={net.id}
        id={{ group, idx }}
        network={net}
        integrations={integrations}
        integrationTitle={groupTitle}
        datatypes={datatypes}
        vertical={vertical}
        onInitNetwork={d3 => {
          // Param reassign used prior to refactor unchanged
          // eslint-disable-next-line no-param-reassign
          net.d3 = d3;
          const d3nets = networks.filter(net => net.d3).map(net => net.d3);
          d3nets.forEach(d3net => {
            d3net.bindNetworks(d3nets);
            d3net.updateStyling();
          });
        }}
        onNetworkFilter={(genesrm, genesadd) =>
          onNetworkFilter(net, genesrm, genesadd)
        }
      />
    </Col>
  );

  const infoPanel = (
    <Alert bsStyle="info" style={{ textAlign: 'center' }}>
      <strong>
        <i className="fa fa-info-circle fa-lg" /> Add a{' '}
        {groupTitle.toLowerCase()} network
      </strong>
      : select your
      {` ${groupTitle.toLowerCase()}`} and genes above and then click
      <span className="fa-stack">
        <i className="fa fa-square fa-stack-2x" />
        <i className="fa fa-plus-circle fa-inverse fa-stack-1x" />
      </span>
    </Alert>
  );

  const progress = (
    <ProgressBar active striped label="Loading available networks" now={100} />
  );

  return (
    <div className={s.root}>
      {(networks.length === 0 && !isLoading && (
        <div className={s.panel}>
          <NetworkSelection
            group={group}
            disabled={disabled}
            integrations={integrations}
            integrationTitle={groupTitle}
            integrationScores={integrationScores}
            datatypes={datatypes}
            onSubmitNetwork={newOnNetworkAdd}
          />
        </div>
      )) ||
        (networks.length === 1 && (
          <ModalDialog
            className={s.addButton}
            title={`Add a ${groupTitle.toLowerCase()} network to compare`}
            disabled={disabled}
            buttonText={
              <div>
                <i className="fa fa-plus-circle" /> Add network
              </div>
            }
            buttonTooltip="Add another network to compare functional interactions"
          >
            <div className={s.panel}>
              <NetworkSelection
                group={group}
                disabled={disabled}
                integrations={integrations}
                integrationTitle={groupTitle}
                integrationScores={integrationScores}
                datatypes={datatypes}
                onSubmitNetwork={newOnNetworkAdd}
              />
            </div>
          </ModalDialog>
        ))}
      {(networks && networks.length && networks.map(networkapps)) ||
        (!disabled && infoPanel) ||
        ((integrations.length === 0 || !selectedIntegration) && progress)}
    </div>
  );
}

const mapStateToProps = (state, props) => {
  const { group } = props;
  const selection = state.networkSelection[group];
  return {
    relevantNetworks: state.relevantNetworks,

    // Currently selected integration
    selectedIntegration: selection && selection.selectedIntegration,

    // Currently selected array of data types
    selectedDatatypes: selection && selection.selectedDatatypes,
  };
};

const mapDispatchToProps = (dispatch, props) => {
  const { group } = props;
  return {
    onNetworkAdd: (integration, datatypes, genes, networks) => {
      dispatch(addNetwork(integration, genes, { group }));
      dispatch(
        selectDatatypes(datatypes, {
          group,
          idx: networks.length,
        }),
      );
      dispatch(
        fetchNetworkWithTerms(
          integration,
          genes,
          { group, idx: networks.length },
          datatypes,
        ),
      );
    },
  };
};

const MultiNetwork = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MultiNetworkView);

export default withStyles(s)(MultiNetwork);
